import { emersonRoutes } from "@emerson/data/emersonRoutes";
import type { CatalogParams } from "@shared/components/CatalogSearch/CatalogSearch";
import type { ParamsOptions } from "@shared/hooks/useUrlParams/useUrlParams";
import { useUrlParams, getParamsString } from "@shared/hooks/useUrlParams/useUrlParams";
import type { To } from "@shared/react-router-dom/react-router-dom";
import { useNavigate, useMatch, useLocation } from "@shared/react-router-dom/react-router-dom";

const useEmersonCatalogParams = (
  initialOptions?: ParamsOptions
): [CatalogParams, (params: CatalogParams, options?: ParamsOptions) => void] => {
  const navigate = useNavigate();
  const location = useLocation();
  const [parseParamsString, setUrlParams] = useUrlParams<CatalogParams>(initialOptions);

  const setUrlFromStaticPage = (params: CatalogParams, options?: ParamsOptions): void => {
    const defaultOptions = {
      pathname: location.pathname,
      replace: false,
      preserveScroll: false,
    };

    const { replace, preserveScroll } = {
      ...defaultOptions,
      ...initialOptions,
      ...options,
    };

    const paramsString = getParamsString<CatalogParams>(params);

    const newLocation: To = {
      pathname: emersonRoutes.shop.catalog,
      search: paramsString,
    };

    navigate(newLocation, {
      replace,
      state: preserveScroll ? { scrollPosition: window.scrollY } : undefined,
    });
  };

  const matchBrand = useMatch(emersonRoutes.staticSearchResultsPage.staticBrandPage);
  const matchCategory = useMatch(emersonRoutes.staticSearchResultsPage.staticCategoryPage);

  if (matchBrand?.params?.brandData) {
    const brandData = matchBrand.params.brandData.split("-");
    const brandId = brandData.pop();
    const brandName = brandData.join(" ");

    const staticCatalogParams = {
      brands: [
        {
          id: brandId,
          name: brandName,
          logo: "",
        },
      ],
    } as CatalogParams;

    return [staticCatalogParams, setUrlFromStaticPage];
  }

  if (matchCategory?.params?.categoryData) {
    const categoryData = matchCategory.params.categoryData.split("-");
    const categoryId = categoryData.pop();
    const categoryName = categoryData.join(" ");

    const staticCatalogParams = {
      taxonomies: [
        {
          id: categoryId,
          name: categoryName,
        },
      ],
    } as CatalogParams;

    return [staticCatalogParams, setUrlFromStaticPage];
  }

  return [parseParamsString, setUrlParams];
};

export { useEmersonCatalogParams };
